import React, { useState, useCallback, useMemo } from 'react';
import { Box, Button, Typography, Loader } from '@lucid/lego';
import { useStore } from '../../../../../store/store';
import { useGetLabels } from '../../../../../hooks/useLabels';
import { convertToContentfulLocale } from '../../../../../utils/tools';
import type { QuoteListItem } from '../../../../../api/services/lupp/prospect/dto.prospect';
import type {
  Quote,
  QuoteVinStatus,
  QuoteStatus,
} from '../../../../../api/services/lupp/quotes/dto.quotes';
import type { Theme } from '../utils';
import Table from './Quotes/Table/Table';
import styles from './QuotationsTab.module.scss';

type QuotationsTabProps = {
  theme: Theme;
  prospectId: string;
  onCreateQuotation: () => void;
  quoteList: QuoteListItem[];
};

/**
 * Quotations tab content component
 * Displays a table of quotes with search and pagination
 * @component
 * @param props - Component props
 * @param props.theme - Theme for the component
 * @param props.prospectId - The prospect identifier to fetch quotes for
 * @param props.onCreateQuotation - Handler for create quotation button click
 * @returns JSX.Element
 * @example
 * <QuotationsTab theme="dark" prospectId="123" onCreateQuotation={() => {}} />
 */
const QuotationsTab: React.FC<QuotationsTabProps> = ({
  theme,
  prospectId,
  onCreateQuotation,
  quoteList,
}) => {
  const locale = useStore((state) => state.locale);
  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: labels,
    isLoading: labelsLoading,
    error: labelsError,
  } = useGetLabels({
    id: '6dhtpdHCYn1jTZ1cRAXtJR', // TODO: Replace with correct Contentful ID for Quotes labels
    locale: convertToContentfulLocale(locale),
  });

  // Map QuoteListItem[] to Quote[] for the Table component
  const mappedQuotes = useMemo<Quote[]>(() => {
    return quoteList.map((item) => ({
      quote_identifier: item.quote_identifier,
      model: item.model,
      trim: item.trim,
      vin: item.vin,
      vin_status: item.vin_status as QuoteVinStatus | null,
      total_amount: item.total_amount,
      payment_type: item.payment_type,
      expiry_date: item.expiry_date || '',
      status: item.status as QuoteStatus,
    }));
  }, [quoteList]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const renderContent = () => {
    if (labelsLoading) {
      return (
        <Box className={styles.loaderContainer}>
          <Loader size={48} />
        </Box>
      );
    }

    if (labelsError) {
      return (
        <Box pt={24}>
          <Typography variant="body" size="large" theme={theme} color="error-1">
            {labels?.error_loading_labels || 'Error loading data'}
          </Typography>
        </Box>
      );
    }

    return (
      <Box pt={24} className={styles.loadingContainer}>
        <Table
          theme={theme === 'dark' ? 'light' : 'dark'}
          data={mappedQuotes}
          labels={labels || {}}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          prospectId={prospectId}
        />
      </Box>
    );
  };

  return (
    <Box pt={16}>
      <Box>
        <Box className={styles.quotesHeader}>
          <Box>
            <Typography variant="headline" size="small" color="white" theme={theme}>
              {labels?.quote_listing_title || 'Quote Listing'}
            </Typography>
            <Typography variant="body" size="medium" color="white" theme={theme}>
              {labels?.quote_listing_description || 'View and manage your quotes'}
            </Typography>
          </Box>
          <Box>
            <Button
              type="primary"
              color="neutral"
              theme={theme}
              dataTestId="create-quotation-button"
              gaComponent="prospect-details"
              label="Create New Quotation"
              handleClick={onCreateQuotation}
            />
          </Box>
        </Box>
        {renderContent()}
      </Box>
    </Box>
  );
};

export default QuotationsTab;
